import React, { useState } from 'react';
import ModalLayout from '../../app-views/components/ModalLayout';
import { Button, message, Space, Steps } from 'antd';
import { useAccountMutation } from '../../../hooks/vendorlink/Account';
import { useForm } from 'react-hook-form';
import { useCurrentUser } from '../../../redux/selector';
import { CreateAccountAndUser } from '@quick/client';
import AccountInformationForm from './AccountInformationForm';
import PrimaryContactForm from './PrimaryContactForm';

enum STEP {
  PRIMARY_CONTACT = 'PRIMARY_CONTACT',
  ACCOUNT_INFORMATION = 'ACCOUNT_INFORMATION',
}

interface CreateFlowProps {
  onComplete: () => void;
  onCancel: () => void;
}

const CreateFlow = ({ onComplete, onCancel }: CreateFlowProps) => {
  const user = useCurrentUser();
  const { control, handleSubmit } = useForm<CreateAccountAndUser>({
    defaultValues: {
      accountUserEmail: user?.email,
    },
  });
  const [step, setStep] = useState(STEP.PRIMARY_CONTACT);
  const { createAccountMutation } = useAccountMutation();

  const onClickNextStep = (step: STEP) => {
    return handleSubmit(() => {
      setStep(step);
    });
  };

  const onSubmit = async (values: CreateAccountAndUser) => {
    try {
      await createAccountMutation.mutateAsync({
        createAccountAndUser: values,
      });
      onComplete();
    } catch (e) {
      message.error(e.message);
    }
  };

  switch (step) {
    case STEP.PRIMARY_CONTACT:
      return (
        <ModalLayout
          header={<CreateFlowProgress step={step} />}
          content={<PrimaryContactForm control={control} />}
          footer={
            <Space className="mt-9">
              <Button onClick={onClickNextStep(STEP.ACCOUNT_INFORMATION)} type="primary">
                Next
              </Button>
              <Button onClick={onCancel} type="text">
                Cancel
              </Button>
            </Space>
          }
        />
      );
    case STEP.ACCOUNT_INFORMATION:
      return (
        <ModalLayout
          header={<CreateFlowProgress step={step} />}
          content={<AccountInformationForm control={control} />}
          footer={
            <Space>
              <Button loading={createAccountMutation.isLoading} onClick={handleSubmit(onSubmit)} type="primary">
                Done
              </Button>
              <Button
                disabled={createAccountMutation.isLoading}
                onClick={() => setStep(STEP.PRIMARY_CONTACT)}
                type="text"
              >
                Previous
              </Button>
            </Space>
          }
        />
      );
  }
};

interface CreateFlowProgressProps {
  step: STEP;
}

const CreateFlowProgress: React.FC<CreateFlowProgressProps> = ({ step }) => {
  let stepIndex = 0;
  switch (step) {
    case STEP.PRIMARY_CONTACT:
      stepIndex = 0;
      break;
    case STEP.ACCOUNT_INFORMATION:
      stepIndex = 1;
      break;
  }
  return (
    <Steps current={stepIndex}>
      <Steps.Step title="About you" />
      <Steps.Step title="Your business" />
    </Steps>
  );
};

export default CreateFlow;
